import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Users, Search, Crown, Sparkles, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import PartnerLayout from '../../components/PartnerLayout';
import { partnerPortalAPI } from '../../lib/partnerApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const StatusBadge = ({ status }) => {
  const cfg = {
    active: 'bg-green-100 text-green-700',
    inactive: 'bg-muted text-gray-600',
    suspended: 'bg-red-100 text-red-700',
  };
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium capitalize', cfg[status] || 'bg-muted text-gray-600')}>
      {status || 'Unknown'}
    </span>
  );
};

const PlanBadge = ({ premium, ai }) => {
  if (premium && ai) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-purple-100 to-blue-100 text-purple-700">Both</span>;
  if (premium) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700"><Crown className="w-3 h-3" />Premium</span>;
  if (ai) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"><Sparkles className="w-3 h-3" />AI Plan</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">Free</span>;
};

const PartnerCustomersPage = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await partnerPortalAPI.customers({ page: pagination.page, limit: pagination.limit, search: search.trim() || undefined });
      setCustomers(res.data.customers || []);
      setPagination(p => ({ ...p, total: res.data.total, pages: res.data.pages }));
    } catch { toast.error('Failed to load customers'); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.limit, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { e.preventDefault(); setPagination(p => ({ ...p, page: 1 })); };

  return (
    <PartnerLayout>
      <div data-testid="partner-customers" className="space-y-6 pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Customers</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Customers who signed up using your referral code</p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={cn('w-4 h-4 mr-2', loading && 'animate-spin')} /> Refresh
          </Button>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-card rounded-xl border border-border p-4 text-center shadow-sm">
            <p className="text-2xl font-bold">{pagination.total}</p>
            <p className="text-xs text-muted-foreground mt-1">Total Referrals</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-purple-600">{customers.filter(c => c.premium_member === 1 || c.ai_plan === 1).length}</p>
            <p className="text-xs text-muted-foreground mt-1">Paid (on page)</p>
          </div>
          <div className="bg-card rounded-xl border border-border p-4 text-center shadow-sm">
            <p className="text-2xl font-bold text-muted-foreground">{customers.filter(c => c.premium_member !== 1 && c.ai_plan !== 1).length}</p>
            <p className="text-xs text-muted-foreground mt-1">Free (on page)</p>
          </div>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="bg-card rounded-xl border border-border p-4 shadow-sm flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search by name or email…" value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="customer-search" />
          </div>
          <Button type="submit">Search</Button>
          {search && <Button type="button" variant="outline" onClick={() => { setSearch(''); setPagination(p => ({ ...p, page: 1 })); }}>Clear</Button>}
        </form>

        {/* Table */}
        <div data-testid="customers-table" className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted">
                <tr>
                  {['Name', 'Status', 'Plan', 'Premium Expiry', 'AI Plan Expiry', 'Joined'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading…</td></tr>
                ) : customers.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No customers found</td></tr>
                ) : (
                  customers.map((c, i) => (
                    <tr key={c.customer_id || i} className="border-t border-border hover:bg-muted">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-semibold text-sm shrink-0">
                            {(c.name || c.email || '?')[0].toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{c.name || 'No name'}</p>
                            <p className="text-xs text-muted-foreground">{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                      <td className="px-4 py-3"><PlanBadge premium={c.premium_member === 1} ai={c.ai_plan === 1} /></td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(c.expiry_date)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(c.ai_plan_expiry)}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">{formatDate(c.joined)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border">
              <p className="text-sm text-muted-foreground">Showing {(pagination.page - 1) * pagination.limit + 1}–{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}</p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1}><ChevronLeft className="w-4 h-4" /></Button>
                <span className="text-sm">Page {pagination.page} of {pagination.pages}</span>
                <Button variant="outline" size="sm" onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </PartnerLayout>
  );
};

export default PartnerCustomersPage;
